/**
 * Gate for the V1/V2 switch.
 *
 * FULL ROLLOUT (2026-07-08): V2 is now the default experience for EVERY user, not
 * just an A/B cohort. V1 is intentionally kept reachable via the switcher as a
 * safety escape hatch (we do not delete the proven V1 app) — users who explicitly
 * switch back to classic keep that choice (persisted). New users and anyone who
 * hasn't chosen default to V2.
 *
 * The old cohort lists are retained only for historical reference / analytics
 * segmentation; they no longer gate access.
 */
export const V2_AB_GROUPS = [14, 15]; // historical: original V2 A/B cohort
export const V2_PRICING_AB_GROUPS = [12, 13]; // historical: pricing-page A/B cohort

/** V2 is available to everyone now (was: cohort 14–15 only). */
export function canUseV2(_abGroup?: number | null): boolean {
  return true;
}

/**
 * The V2 pricing page (UpgradePage — the live-counter design) is now shown to
 * EVERYONE, so pricing is identical across all groups. Only consulted from the V1
 * router (PricingRoute); the V2 app already routes /subscription to UpgradePage.
 */
export function showsV2PricingPage(_abGroup?: number | null): boolean {
  return true;
}

/**
 * A/B (pricing page, mobile only): the lower half of the 16 ab groups ([0,7])
 * gets a horizontally swipeable plan carousel on the pricing page instead of
 * the stacked cards — centered on MAX by default, with peek/dots/arrows/nudge
 * cues so it's obvious the other plans scroll sideways. Groups 8–15 keep the
 * classic stacked layout. Desktop is unaffected for everyone.
 *
 * TEST ENDED (2026-08-16): the carousel didn't perform, so it's turned OFF for
 * everyone — all ab groups now see the classic stacked layout. The cohort logic
 * below is intentionally retained (not deleted) in case we revisit it; we just
 * short-circuit to false above it.
 */
export function usesSwipeablePricingCards(abGroup?: number | null): boolean {
  // Flip back to true to re-run the carousel A/B for cohort 0–7.
  const CAROUSEL_TEST_ENABLED = false;
  return (
    CAROUSEL_TEST_ENABLED &&
    typeof abGroup === 'number' &&
    Number.isFinite(abGroup) &&
    abGroup >= 0 &&
    abGroup <= 7
  );
}

/**
 * The UI version shown to a user: ALWAYS 'v2' now (full rollout, no switcher).
 * There is deliberately no way for a user to switch back to V1 — the argument is
 * ignored, so even a stale persisted 'v1' choice from an old build can't pin
 * anyone to the classic app. V1 code is retained (not deleted) but never routed.
 */
export function resolveUiVersion(
  _choice?: 'v1' | 'v2' | null | undefined,
  _abGroup?: number | null,
): 'v1' | 'v2' {
  return 'v2';
}
